import { type BaInpaintImage, BaInpaintTaskStatus } from '@/typings/ai'
import type { AITask } from '@/typings/base'
import { isElectron } from '@/utils/common'
import type { InpaintImageInfo } from '@/typings/image'
import { ImageOrigin } from '@/constants/image'
import type { ImageRenderToolbarProps } from 'naive-ui'

// 导入 WASM 模块
// @ts-ignore
import * as maskProcessorModule from '../wasm/mask_processor/pkg/mask_processor'
// @ts-ignore
import initMaskProcessor from '../wasm/mask_processor/pkg/mask_processor'

// WASM 模块初始化状态
let wasmInitPromise: Promise<any> | null = null;
let wasmInitialized = false;

// 初始化 WASM 模块
const initWasm = async () => {
  if (wasmInitPromise === null) {
    wasmInitPromise = initMaskProcessor().then(() => {
      wasmInitialized = true;
      console.log('Mask processor WASM initialized successfully');
    }).catch(error => {
      console.error('Failed to initialize WASM module', error);
      // 重置，允许重试
      wasmInitPromise = null;
    });
  }
  return wasmInitPromise;
}

export async function hashImage(imageUrl: string): Promise<string> {
  try {
    const response = await fetch(imageUrl)
    const buffer = await response.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  } catch (e) { 
    console.error('Failed to calculate image hash:', e)
    return ''
  }
}

export function dataUriToBlob(dataUri: string): Blob {
  const splitDataURI = dataUri.split(',')
  const byteString =
    splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1])
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0]

  const ia = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)

  return new Blob([ia], { type: mimeString })
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert blob to base64'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export async function convertBlobUrlToBlob(url: string): Promise<Blob> {
  try {
    const response = await fetch(url)
    return await response.blob()
  } catch (error) {
    console.error('转换URL到Blob时出错:', error)
    throw error
  }
}

export async function urlToBlob(url: string): Promise<Blob> {
  const response = await fetch(url)
  return await response.blob()
}

export function isImageNotComplete(image: InpaintImageInfo) {
  return image.origin !== ImageOrigin.USER_UPLOAD && image.status !== BaInpaintTaskStatus.completed
}

export function isImageNotReady(image: InpaintImageInfo) {
  return image.origin !== ImageOrigin.USER_UPLOAD &&
    (image.status === BaInpaintTaskStatus.inQueue || image.status === BaInpaintTaskStatus.inProgress || image.status === BaInpaintTaskStatus.inWaiting)
}

export function isInpaintTaskComplete(task: AITask<BaInpaintImage[]>): boolean {
  const { images: subTasks } = task || {}

  for (const subTask of subTasks || []) {
    if (
      subTask.status !== BaInpaintTaskStatus.completed &&
      subTask.status !== BaInpaintTaskStatus.failed
    ) {
      return false
    }
  }

  return true
}

export interface ImageInfo {
  src: string
  width: number
  height: number
  image: HTMLImageElement
}

export function getImageInfoFromFile(file: File | string): Promise<ImageInfo> {
  let src: string
  if (file instanceof File) {
    // TODO revokeObjectURL()
    src = URL.createObjectURL(file)
  } else {
    if (file.startsWith('http')) {
      // eslint-disable-next-line no-restricted-globals
      src = new URL(file, location.origin).href
    } else {
      src = file
    }
  }

  return new Promise((resolve) => {
    const image = new Image()
    image.src = src
    image.onload = () => {
      resolve({
        src,
        width: image.naturalWidth,
        height: image.naturalHeight,
        image: image
      })
    }
  })
}

// export function imageToBase64(image: string): string {
//   if (isBase64(image, { mimeRequired: true })) {
//     return image
//   } else {
//     // TODO
//     return ''
//   }
// }

export function avatar(options: any) {
  const image = options.image
  const maskImage = options.maskImage
  let vertical = false
  if (!image) {
    console.error('Image cannot be empty!')
    return
  } else if (typeof image != 'string') {
    console.error('Image must be a string type!')
    return
  }
  if (maskImage != '' && typeof maskImage != 'undefined') {
    if (typeof maskImage != 'string') {
      console.error('maskImage must be a string type!')
      return
    } else {
      vertical = true
    }
  } else {
    vertical = false
  }
  const option = extend(
    {
      exportType: 'base64',
      success() {
      },
      error() {
      }
    },
    options
  )
  const maskImg = new Image()
  maskImg.crossOrigin = '*'

  maskImg.onload = function() {
    const maskCanvas = document.createElement('canvas'),
      maskCtx = maskCanvas.getContext('2d') as CanvasRenderingContext2D
    maskCanvas.width = maskImg.width
    maskCanvas.height = vertical ? maskImg.height : maskImg.height / 2
    maskCtx!.drawImage(
      maskImg,
      0,
      vertical ? 0 : maskCanvas.height,
      maskImg.width,
      maskCanvas.height,
      0,
      0,
      maskImg.width,
      maskCanvas.height
    )
    const maskImageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height)

    const originImg = new Image()
    originImg.crossOrigin = '*'

    originImg.onload = function() {
      const originCanvas = document.createElement('canvas'),
        originCtx = originCanvas.getContext('2d') as CanvasRenderingContext2D
      originCanvas.width = vertical ? maskImg.width : originImg.width
      originCanvas.height = vertical ? maskCanvas.height : originImg.height / 2
      originCtx.drawImage(
        originImg,
        0,
        0,
        originImg.width,
        vertical ? originImg.height : originImg.height / 2,
        0,
        0,
        vertical ? maskImg.width : originImg.width,
        vertical ? maskImg.height : originImg.height / 2
      )
      const originImgData = originCtx.getImageData(0, 0, originCanvas.width, originCanvas.height),
        resultBase64 = get_alpha_image(originImgData, maskImageData, option)

      option.success(resultBase64)
    }

    originImg.src = image

    originImg.onerror = () => {
      option.error({ code: 0, msg: 'load origin image error' })
    }
  }

  maskImg.src = `${vertical ? maskImage : image}`

  maskImg.onerror = () => {
    option.error({ code: 0, msg: 'load mask image error' })
  }
}

function extend(opt1: any, opt2: any) {
  for (const i in opt2) {
    // eslint-disable-next-line no-prototype-builtins
    if (opt2.hasOwnProperty(i)) {
      if (typeof opt2[i] == 'object') {
        if (typeof opt1[i] !== 'object' || opt1[i] === null) {
          opt1[i] = {}
        }
        extend(opt1[i], opt2[i])
      } else {
        opt1[i] = opt2[i]
      }
    }
  }
  return opt1
}

function get_alpha_image(srcImage: any, maskImage: any, option: any) {
  const canvas = document.createElement('canvas'),
    result = canvas.getContext('2d')!.createImageData(srcImage)
  const size = srcImage.width * srcImage.height * 4,
    MT_RED = 0,
    MT_GREEN = 1,
    MT_BLUE = 2,
    MT_ALPHA = 3

  for (let i = 0; i < size; i += 4) {
    result.data[i + MT_RED] = srcImage.data[MT_RED + i]
    result.data[i + MT_GREEN] = srcImage.data[MT_GREEN + i]
    result.data[i + MT_BLUE] = srcImage.data[MT_BLUE + i]
    result.data[i + MT_ALPHA] = maskImage.data[i]
  }

  const resultCanvas = document.createElement('canvas'),
    resultCtx = resultCanvas.getContext('2d') as CanvasRenderingContext2D
  resultCanvas.width = result.width
  resultCanvas.height = result.height
  resultCtx.putImageData(result, 0, 0, 0, 0, resultCanvas.width, resultCanvas.height)

  if (option.exportType == 'canvas') {
    return resultCanvas
  } else {
    return resultCanvas.toDataURL('image/png')
  }
}

/**
 * 将png图片的多余透明区域去除，保留存在像素点的最小矩形
 * @param imageURL
 * @returns 返回处理后的图片URL和在原图中的位置坐标
 */
export function removeRedundant(imageURL: string): Promise<{dataURL: string, position: {x: number, y: number}}> {
  return new Promise<{dataURL: string, position: {x: number, y: number}}>((resolve, reject) => {
    const img = new Image()
    urlParser(imageURL).then((url) => {
      img.src = url
      img.crossOrigin = 'anonymous'
      img.onload = function() {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data
        let minX = canvas.width,
          minY = canvas.height,
          maxX = 0,
          maxY = 0

        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const alpha = data[(canvas.width * y + x) * 4 + 3]
            if (alpha > 0) {
              minX = Math.min(minX, x)
              minY = Math.min(minY, y)
              maxX = Math.max(maxX, x)
              maxY = Math.max(maxY, y)
            }
          }
        }

        const width = maxX - minX + 1
        const height = maxY - minY + 1
        const trimmedCanvas = document.createElement('canvas')
        const trimmedCtx = trimmedCanvas.getContext('2d') as CanvasRenderingContext2D
        trimmedCanvas.width = width
        trimmedCanvas.height = height
        trimmedCtx.drawImage(img, minX, minY, width, height, 0, 0, width, height)

        resolve({
          dataURL: trimmedCanvas.toDataURL('image/png'),
          position: { x: minX, y: minY }
        })
      }

      img.onerror = (error) => reject(error)
    })
  })
}

export function hexToRgb(hex: string) {
  const r = parseInt(hex.substring(1, 3), 16)
  const g = parseInt(hex.substring(3, 5), 16)
  const b = parseInt(hex.substring(5, 7), 16)
  return [r, g, b]
}

// export function hexToCMYK(hex: string) {
//   let c = 0
//   let m = 0
//   let y = 0
//   let k = 0
//
//   hex = hex.charAt(0) == '#' ? hex.substring(1, 7) : hex
//
//   if (hex.length != 6) {
//     console.error('Invalid length of the input hex value!')
//     return
//   }
//   if (!/[0-9a-f]{6}/i.test(hex)) {
//     console.error('Invalid digits in the input hex value!')
//     return
//   }
//
//   const r = parseInt(hex.substring(0, 2), 16)
//   const g = parseInt(hex.substring(2, 4), 16)
//   const b = parseInt(hex.substring(4, 6), 16)
//
//   // BLACK
//   if (r == 0 && g == 0 && b == 0) {
//     k = 1
//     return [0, 0, 0, 1]
//   }
//
//   c = 1 - r / 255
//   m = 1 - g / 255
//   y = 1 - b / 255
//
//   const minCMY = Math.min(c, Math.min(m, y))
//
//   c = Math.round(((c - minCMY) / (1 - minCMY)) * 100) / 100
//   m = Math.round(((m - minCMY) / (1 - minCMY)) * 100) / 100
//   y = Math.round(((y - minCMY) / (1 - minCMY)) * 100) / 100
//   k = Math.round(minCMY * 100) / 100
//
//   return [c, m, y, k]
// }

export function lineDraftHandler(url: string): Promise<string> {
  return new Promise((resolve) => {
    urlParser(url).then((u) => {
      // 创建新的Image对象
      const img = new Image()
      img.crossOrigin = 'Anonymous'
      img.src = u // 替换为你的图片路径

      img.onload = function() {
        // 创建canvas元素，并获取2D上下文
        const canvas: HTMLCanvasElement = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d')

        // 将图片绘制到canvas上
        if (ctx) {
          ctx.drawImage(img, 0, 0, img.width, img.height)

          // 获取图像数据
          const imageData: ImageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data: Uint8ClampedArray = imageData.data

          for (let i: number = 0; i < data.length; i += 4) {
            // 对每个颜色通道进行反转，255减去原有值
            data[i] = 255 - data[i] // Red channel
            data[i + 1] = 255 - data[i + 1] // Green channel
            data[i + 2] = 255 - data[i + 2] // Blue channel

            // 检查颜色值，如果是白色或接近白色（反转后），则设为透明
            if (data[i] > 200 && data[i + 1] > 200 && data[i + 2] > 200) {
              data[i + 3] = 0
            }
          }

          // 写入新的图像数据
          ctx.putImageData(imageData, 0, 0)

          resolve(canvas.toDataURL())
        }
      }
    })
  })
}

export function drawImageToCanvas(
  imageURL: string,
  rect?: { canvasWidth: number; canvasHeight: number }
): Promise<HTMLCanvasElement> {
  return new Promise((resolve) => {
    urlParser(imageURL).then((url) => {
      const image = new Image()
      image.crossOrigin = 'anonymous' // u6dfbu52a0u8de8u57dfu5c5eu6027
      if (rect) {
        image.width = rect.canvasWidth
        image.height = rect.canvasHeight
      }
      image.src = url

      image.onload = () => {
        // 创建一个新的canvas
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = image.width
        canvas.height = image.height
        // 在canvas上画出图片
        ctx!.drawImage(image!, 0, 0, image.width, image.height)
        resolve(canvas)
      }
    })
  })
}

/**
 * 缩放画布图片尺寸
 * @param canvas
 * @param imageWidth
 * @param imageHeight
 */
export const resizeImage = (canvas: HTMLCanvasElement, imageWidth: number, imageHeight: number) => {
  // 创建一个新的canvas
  const newCanvas = document.createElement('canvas')
  const ctx = newCanvas.getContext('2d')

  // 设置新canvas的尺寸
  newCanvas.width = imageWidth
  newCanvas.height = imageHeight

  // 在新canvas上画出裁剪后的图片
  ctx!.drawImage(canvas!, 0, 0, imageWidth, imageHeight)

  return newCanvas
}

/**
 * 生成蒙版
 * @param mask
 * @param invert
 */
// export const maskHandler = (mask: string, invert = false): Promise<string> => {
//   return new Promise((resolve, reject) => {
//     urlParser(mask).then((url) => {
//       const img = new Image()
//       img.src = url
//       img.crossOrigin = 'anonymous'
//       img.onload = async () => {
//         const canvas = document.createElement('canvas')
//         canvas.height = img.height
//         canvas.width = img.width
//         const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
//         ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, img.width, img.height)
//         const maskData = ctx.getImageData(0, 0, canvas.width, canvas.height)
//         for (let i = 0; i < maskData!.data.length; i += 4) {
//           // 当该像素是透明度小于0.8的，则设置成黑色，透明度大于0.8的则变成白色，抠图工具需要这样
//           if (maskData.data[i + 3] < 255 * 0.8) {
//             maskData!.data[i] = 0
//             maskData!.data[i + 1] = 0
//             maskData!.data[i + 2] = 0
//             maskData!.data[i + 3] = 255
//           } else {
//             maskData!.data[i] = 255
//             maskData!.data[i + 1] = 255
//             maskData!.data[i + 2] = 255
//             maskData!.data[i + 3] = 255
//           }
//         }
//         ctx.putImageData(maskData, 0, 0)
//
//         const imageData = canvas.toDataURL('image/png')
//         const result = invert ? await invertImageColor(imageData) : imageData
//
//         resolve(result)
//       }
//
//       img.onerror = () => reject('蒙版处理失败')
//     })
//   })
// }

/**
 * 合并蒙版
 * @param imgUrl1
 * @param imgUrl2
 */
export const mergeMasks = async (imgUrl1: string, imgUrl2: string): Promise<string> => {
  try {
    // u52a0u8f7du4e24u5f20u56feu7247u5e76u786eu4fddu6709CORSu5c5eu6027
    const [img1, img2] = await Promise.all([
      loadImage(imgUrl1),
      loadImage(imgUrl2)
    ]);

    // 确保 WASM 模块已初始化
    if (!wasmInitialized) {
      await initWasm();
      console.log('wasmInitialized', wasmInitialized)
    }

    const canvas1 = document.createElement('canvas')
    const ctx1 = canvas1.getContext('2d')!
    canvas1.width = img1.naturalWidth
    canvas1.height = img1.naturalHeight
    ctx1.drawImage(img1, 0, 0)

    const canvas2 = document.createElement('canvas')
    const ctx2 = canvas2.getContext('2d')!
    canvas2.width = img2.naturalWidth
    canvas2.height = img2.naturalHeight
    ctx2.drawImage(img2, 0, 0)

    // 获取图像数据
    const imgData1 = ctx1.getImageData(0, 0, canvas1.width, canvas1.height)
    const imgData2 = ctx2.getImageData(0, 0, canvas2.width, canvas2.height)

    // 使用 WASM 模块处理图像数据
    let resultData: Uint8ClampedArray;
    
    if (wasmInitialized) {
      try {
        // 调用 WASM 函数处理图像数据
        const result = maskProcessorModule.merge_images(
          new Uint8Array(imgData1.data.buffer), 
          new Uint8Array(imgData2.data.buffer), 
          canvas1.width, 
          canvas1.height
        );
        resultData = new Uint8ClampedArray(result.buffer);
        console.info('merge with wasm success')
      } catch (error) {
        console.error('Error using WASM to merge images:', error);
        // 回退到原始 JavaScript 实现
        return fallbackMergeMasks(imgData1, imgData2, canvas1.width, canvas1.height);
      }
    } else {
      // 回退到原始 JavaScript 实现
      return fallbackMergeMasks(imgData1, imgData2, canvas1.width, canvas1.height);
    }

    // 创建输出 Canvas
    const canvas3 = document.createElement('canvas')
    const ctx3 = canvas3.getContext('2d')!
    canvas3.width = canvas1.width
    canvas3.height = canvas1.height

    // 将合并后的数据放入新的 ImageData 对象
    const outputImageData = new ImageData(resultData, canvas1.width, canvas1.height);
    ctx3.putImageData(outputImageData, 0, 0);

    // 返回 Data URL
    return canvas3.toDataURL('image/png');
  } catch (error) {
    console.error('Error merging images:', error);
    return '';
  }
}

// 原始 JavaScript 实现作为回退方案
const fallbackMergeMasks = (
  imgData1: ImageData, 
  imgData2: ImageData, 
  width: number, 
  height: number
): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  canvas.width = width;
  canvas.height = height;
  
  // 创建新的图像数据
  const outputImageData = ctx.createImageData(width, height);
  const data1 = imgData1.data;
  const data2 = imgData2.data;
  const outData = outputImageData.data;

  for (let i = 0; i < data1.length; i += 4) {
    const alpha1 = data1[i + 3];
    const alpha2 = data2[i + 3];

    // 如果两张图片在这个位置都是非透明的，合并它们
    if (alpha1 > 0 && alpha2 > 0) {
      outData[i] = (data1[i] + data2[i]) / 2;
      outData[i + 1] = (data1[i + 1] + data2[i + 1]) / 2;
      outData[i + 2] = (data1[i + 2] + data2[i + 2]) / 2;
      outData[i + 3] = Math.max(alpha1, alpha2);
    } else if (alpha1 > 0) {
      outData[i] = data1[i];
      outData[i + 1] = data1[i + 1];
      outData[i + 2] = data1[i + 2];
      outData[i + 3] = alpha1;
    } else if (alpha2 > 0) {
      outData[i] = data2[i];
      outData[i + 1] = data2[i + 1];
      outData[i + 2] = data2[i + 2];
      outData[i + 3] = alpha2;
    }
  }

  ctx.putImageData(outputImageData, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * 两张png蒙版图片，大小完全一致，我希望将两张图片合并，第二张图片非透明部分替换掉第一张图片对应位置的内容，生成新的png图片
 * @param base64Image1
 * @param base64Image2
 */
export async function mergeImages(base64Image1: string, base64Image2: string): Promise<string> {
  try {
    // u4f7fu7528u6539u8fdbu540eu7684loadImageu51fdu6570u52a0u8f7du56feu7247uff0cu5df2u7ecfu5177u6709CORSu652fu6301
    const [image1, image2] = await Promise.all([
      loadImage(base64Image1),
      loadImage(base64Image2)
    ]);

    // 创建 canvas 用于获取图像数据
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error('Canvas 2D context not available')
    }

    // 设置 canvas 尺寸
    canvas.width = image1.width
    canvas.height = image1.height

    // 获取第一张图片的数据
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image1, 0, 0)
    const imgData1 = context.getImageData(0, 0, canvas.width, canvas.height)

    // 获取第二张图片的数据
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image2, 0, 0)
    const imgData2 = context.getImageData(0, 0, canvas.width, canvas.height)

    // 使用 WASM 处理图像数据
    let resultData: Uint8ClampedArray;
    
    if (wasmInitialized) {
      try {
        // 调用 WASM 函数处理图像数据
        const result = maskProcessorModule.merge_image_with_rect(
          new Uint8Array(imgData1.data.buffer),
          new Uint8Array(imgData2.data.buffer),
          canvas.width,
          canvas.height
        );
        resultData = new Uint8ClampedArray(result.buffer);
        console.info('merge_image_with_rect success')
      } catch (error) {
        console.error('Error using WASM to merge images with rect:', error);
        // 回退到原始 JavaScript 实现
        return fallbackMergeImages(image1, image2);
      }
    } else {
      console.info('merge_image_with_rect fallback')
      // 回退到原始 JavaScript 实现
      return fallbackMergeImages(image1, image2);
    }

    // 创建输出 Canvas
    context.clearRect(0, 0, canvas.width, canvas.height)
    
    // 将合并后的数据放入新的 ImageData 对象
    const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
    context.putImageData(outputImageData, 0, 0);

    // 返回 Data URL
    return canvas.toDataURL();
  } catch (error) {
    console.error('Error merging images:', error);
    return '';
  }
}

// 原始 JavaScript 实现作为回退方案
const fallbackMergeImages = (
  image1: HTMLImageElement,
  image2: HTMLImageElement
): string => {
  // 创建 canvas 元素
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')!

  // 设置 canvas 尺寸
  canvas.width = image1.width
  canvas.height = image1.height

  // 绘制第一张图片到 canvas
  context.drawImage(image1, 0, 0)

  // 绘制第二张图片到 canvas
  context.clearRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image2, 0, 0)

  // 获取第二张图片的像素数据
  const imageData2 = context.getImageData(0, 0, canvas.width, canvas.height)

  // 找到第二张图片的非透明区域的最小矩形区域
  let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const index = (y * canvas.width + x) * 4
      if (imageData2.data[index + 3] !== 0) { // 非透明像素
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }
  }

  // 如果没有找到非透明区域，则直接返回第一张图片
  if (minX > maxX || minY > maxY) {
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image1, 0, 0)
    return canvas.toDataURL()
  }

  // 提取并替换图片2的最小矩形区域到图片1的对应位置
  const width = maxX - minX + 1
  const height = maxY - minY + 1

  const subImageData2 = context.getImageData(minX, minY, width, height)

  // 在替换前重绘第一张图片
  context.clearRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image1, 0, 0)

  // 将图片2的矩形区域绘制到图片1对应的位置
  context.putImageData(subImageData2, minX, minY)

  // 返回合并后的图片 base64 编码
  return canvas.toDataURL()
}

/**
 * 加载图片
 * @param base64 - 图片的 base64 编码
 * @returns Promise<HTMLImageElement>
 */
function loadImage(base64: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = base64;
  });
}

/**
 * 图片颜色取反
 * @param url
 */
// export function invertImageColor(url: string): Promise<string> {
//   return new Promise((resolve, reject) => {
//     urlParser(url).then((u) => {
//       const img = new Image()
//       img.src = u
//
//       img.onload = function() {
//         const canvas = document.createElement('canvas')
//         const ctx = canvas.getContext('2d')!
//
//         canvas.width = img.width
//         canvas.height = img.height
//
//         ctx.drawImage(img, 0, 0, img.width, img.height)
//
//         const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
//         const data = imageData.data
//
//         for (let i = 0; i < data.length; i += 4) {
//           data[i] = 255 - data[i] // r
//           data[i + 1] = 255 - data[i + 1] // g
//           data[i + 2] = 255 - data[i + 2] // b
//         }
//
//         ctx.putImageData(imageData, 0, 0)
//
//         resolve(canvas.toDataURL('image/png'))
//       }
//
//       img.onerror = function(err) {
//         reject(err)
//       }
//     })
//   })
// }

/**
 * 获取图片尺寸
 * @param image
 */
export async function getImageDimensions(
  image: File | string
): Promise<{ width: number; height: number; image: string; dom: HTMLImageElement }> {
  let imgSrc: string
  if (typeof image === 'string') {
    imgSrc = await urlParser(image)
  } else {
    imgSrc = URL.createObjectURL(image)
  }
  return new Promise((resolve, reject) => {

    const img = document.createElement('img')
    img.onload = function() {
      resolve({ width: img.width, height: img.height, image: img.src, dom: img })
    }
    img.onerror = function() {
      reject(new Error('Cannot load image'))
    }
    img.src = imgSrc
  })
}

export function getScaledImageSize(img: HTMLImageElement): { width: number; height: number } {
  const aspectRatio: number = img.naturalWidth / img.naturalHeight

  let scaledWidth: number, scaledHeight: number
  if (img.width / img.height > aspectRatio) {
    // 宽度太宽，所以我们按高度缩放
    scaledWidth = img.height * aspectRatio
    scaledHeight = img.height
  } else {
    // 高度太高，所以我们按宽度缩放
    scaledWidth = img.width
    scaledHeight = img.width / aspectRatio
  }

  return { width: scaledWidth, height: scaledHeight }
}

/**
 * 合并图片
 * @param imageURL1
 * @param imageURL2
 */
// export async function mergeImages(imageURL1: string, imageURL2: string) {
//   const img1 = new Image()
//   const img2 = new Image()
//
//   img1.src = await urlParser(imageURL1)
//   img1.crossOrigin = 'anonymous'
//   img2.src = await urlParser(imageURL2)
//   img2.crossOrigin = 'anonymous'
//
//   await Promise.all([
//     new Promise((resolve) => (img1.onload = resolve)),
//     new Promise((resolve) => (img2.onload = resolve))
//   ])
//
//   const canvas = document.createElement('canvas')
//   const ctx = canvas.getContext('2d')
//
//   if (ctx === null) {
//     new Error('无法获取canvas上下文')
//     return
//   }
//
//   canvas.width = img1.naturalWidth
//   canvas.height = img1.naturalHeight
//
//   let img2Width: number
//   let img2Height: number
//
//   if (img1.naturalWidth > img1.naturalHeight) {
//     const scale = img1.naturalWidth / img2.naturalWidth
//     img2Width = img1.naturalWidth
//     img2Height = img2.naturalHeight * scale
//   } else {
//     const scale = img1.naturalHeight / img2.naturalHeight
//     img2Width = img2.naturalWidth * scale
//     img2Height = img1.naturalHeight
//   }
//
//   const dx = (canvas.width - img2Width) / 2
//   const dy = (canvas.height - img2Height) / 2
//
//   ctx.drawImage(img2, dx, dy, img2Width, img2Height)
//   ctx.drawImage(img1, 0, 0, img1.naturalWidth, img1.naturalHeight)
//
//   return canvas.toDataURL('image/png')
// }

// export async function subtractImages(imageURL1: string, imageURL2: string): Promise<string> {
//   const img1 = new Image()
//   const img2 = new Image()
//
//   img1.src = await urlParser(imageURL1)
//   img1.crossOrigin = 'anonymous'
//   img2.src = await urlParser(imageURL2)
//   img2.crossOrigin = 'anonymous'
//
//   await Promise.all([
//     new Promise((resolve) => (img1.onload = resolve)),
//     new Promise((resolve) => (img2.onload = resolve))
//   ])
//
//   // 创建 canvas 用于处理图片
//   const canvas = document.createElement('canvas')
//   const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D
//
//   // 设定 canvas 的大小与图片一致
//   canvas.width = img1.width
//   canvas.height = img1.height
//
//   // 绘制第一张图片
//   ctx.drawImage(img1, 0, 0)
//
//   // 设置合成操作
//   ctx.globalCompositeOperation = 'destination-out'
//
//   // 绘制第二张图片
//   ctx.drawImage(img2, 0, 0)
//
//   return canvas.toDataURL('image/png')
// }

/**
 * 将图片非透明部分修改成指定颜色
 * @param imageUrl
 * @param hexColor
 */
export function changeColor(imageUrl: string, hexColor: string): Promise<string> {
  return new Promise((resolve, reject) => {
    urlParser(imageUrl).then((url) => {
      const newColor = hexToRgb(hexColor)

      const img = new Image()
      img.crossOrigin = 'anonymous' // 设置跨域属性，以防止安全错误
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')!

        canvas.width = img.width
        canvas.height = img.height

        context.drawImage(img, 0, 0, img.width, img.height)

        const imageData = context.getImageData(0, 0, img.width, img.height)
        const data = imageData.data

        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] !== 0) {
            data[i] = newColor[0]
            data[i + 1] = newColor[1]
            data[i + 2] = newColor[2]
          }
        }

        context.putImageData(imageData, 0, 0)
        resolve(canvas.toDataURL())
      }

      img.onerror = (error) => reject(error)
      img.src = url
    })
  })
}

/**
 * 黑色到白色的灰度图片，将颜黑色/白色部分转换成透明，将白色/黑色部分转换成hexColor，将灰色部分按照灰度程度转换成有一定透明度的hexColor
 * @param imageUrl
 * @param hexColor
 * @param reverse
 */
export function changeTargetColor(
  imageUrl: string,
  hexColor: string,
  reverse = false
): Promise<string> {
  return new Promise((resolve, reject) => {
    // 确保 WASM 模块已初始化
    if (!wasmInitialized) {
      initWasm().then(() => {
        console.log('wasmInitialized', wasmInitialized);
        // 递归调用自身，确保 WASM 已初始化
        changeTargetColor(imageUrl, hexColor, reverse).then(resolve).catch(reject);
      }).catch(error => {
        console.error('Failed to initialize WASM', error);
        // 在初始化失败的情况下继续执行原始实现
        executeChangeTargetColor(imageUrl, hexColor, reverse).then(resolve).catch(reject);
      });
      return;
    }

    // WASM 已初始化，执行转换
    executeChangeTargetColor(imageUrl, hexColor, reverse).then(resolve).catch(reject);
  });
}

// 实际执行转换的函数
function executeChangeTargetColor(
  imageUrl: string,
  hexColor: string,
  reverse = false
): Promise<string> {
  return new Promise((resolve, reject) => {
    urlParser(imageUrl).then((url) => {
      const newColor = hexToRgb(hexColor);

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;

        canvas.width = img.width;
        canvas.height = img.height;

        context.drawImage(img, 0, 0, img.width, img.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        let resultData: Uint8ClampedArray;
        
        if (wasmInitialized) {
          try {
            // 使用 WASM 函数处理图像数据
            const result = maskProcessorModule.change_target_color(
              new Uint8Array(imageData.data.buffer),
              canvas.width,
              canvas.height,
              newColor[0],
              newColor[1],
              newColor[2],
              reverse
            );
            resultData = new Uint8ClampedArray(result.buffer);
            console.info('change_target_color with wasm success');
          } catch (error) {
            console.error('Error using WASM to change target color:', error);
            // 回退到 JavaScript 实现
            resultData = applyChangeTargetColorJS(imageData.data, newColor, reverse);
          }
        } else {
          console.info('change_target_color fallback');
          // 回退到 JavaScript 实现
          resultData = applyChangeTargetColorJS(imageData.data, newColor, reverse);
        }
        
        // 更新图像数据
        const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
        context.putImageData(outputImageData, 0, 0);

        const contentType = await getImageType(url);
        resolve(canvas.toDataURL(contentType));
      };

      img.onerror = (error) => reject(error);
      img.src = url;
    });
  });
}

// JavaScript 版本的图像处理，作为回退
function applyChangeTargetColorJS(
  data: Uint8ClampedArray,
  newColor: number[],
  reverse: boolean
): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(data.length);
  result.set(data);
  
  // 进行颜色反转
  if (!reverse) {
    for (let i = 0; i < result.length; i += 4) {
      result[i] = 255 - result[i]; // 反转红色通道
      result[i + 1] = 255 - result[i + 1]; // 反转绿色通道
      result[i + 2] = 255 - result[i + 2]; // 反转蓝色通道
    }
  }

  // 循环遍历每个像素
  for (let i = 0; i < result.length; i += 4) {
    const r = result[i];
    const g = result[i + 1];
    const b = result[i + 2];

    // 计算灰度值
    const gray = Math.round((r + g + b) / 3);

    // 根据灰度值设置像素的颜色和透明度
    if (gray === 255) { // 白色部分
      result[i + 3] = 0; // 设置透明度为0，即透明
    } else if (gray === 0) { // 黑色部分
      result[i] = newColor[0];
      result[i + 1] = newColor[1];
      result[i + 2] = newColor[2];
      result[i + 3] = 255; // 设置透明度为255，即不透明
    } else { // 灰色部分
      result[i] = newColor[0];
      result[i + 1] = newColor[1];
      result[i + 2] = newColor[2];
      result[i + 3] = 255 - gray; // 设置透明度，灰度越深，透明度越低
    }
  }
  
  return result;
}

export async function urlParser(imageURL: string) {
  if (!isElectron() && imageURL.startsWith('http')) {
    return imageURL.includes('?') ? `${imageURL}&time=${new Date().getTime()}` : `${imageURL}?time=${new Date().getTime()}`
  }
  return imageURL
}

export async function getImageType(url: string) {
  const contentType = await getMimeTypeFromHead(url)

  // 暂时仅支持jpeg和png
  if (contentType === 'image/jpeg') {
    return contentType
  }
  return 'image/png'
}

export async function getMimeTypeFromHead(url: string) {
  try {
    const response = await fetch(await urlParser(url), { method: 'HEAD' });
    return response.headers.get('Content-Type');  // 获取 Content-Type 头部
  } catch (error) {
    console.warn('Error fetching image:', error);
    return 'unknown';
  }
}

// /**
//  * 计算图片非透明像素点占比
//  * @param imageUrl
//  */
// export async function calculateOpaquePixels(imageUrl: string): Promise<number> {
//   const img = new Image()
//   img.crossOrigin = 'Anonymous' // 跨域处理
//   img.src = await urlParser(imageUrl)
//
//   await new Promise((resolve) => {
//     img.onload = resolve
//   })
//
//   const canvas = document.createElement('canvas')
//   const ctx = canvas.getContext('2d')!
//   canvas.width = img.width
//   canvas.height = img.height
//
//   ctx.drawImage(img, 0, 0, img.width, img.height)
//
//   const imageData = ctx.getImageData(0, 0, img.width, img.height)
//   const data = imageData.data
//   let opaquePixels = 0
//
//   for (let i = 0; i < data.length; i += 4) {
//     if (data[i + 3] !== 0) {
//       // Alpha 值不等于0，说明该像素不透明
//       opaquePixels++
//     }
//   }
//
//   const totalPixels = img.width * img.height
//   const ratio = opaquePixels / totalPixels
//
//   return ratio * 100
// }

export async function resizeImage4Base64(
  imageURL: string,
  width: number,
  height: number
): Promise<string> {
  const canvas = await drawImageToCanvas(imageURL)
  const contentType = await getImageType(imageURL)
  const newCanvas = resizeImage(canvas, width, height)
  return newCanvas.toDataURL(contentType)
}

// 将图片的非透明内容向外放大指定像素，然后返回dataURL
// export function expandAndConvertToDataURL(imageUrl: string, expandBy: number): Promise<string> {
//   return new Promise((resolve, reject) => {
//     urlParser(imageUrl).then((url) => {
//       const img = new Image()
//       img.crossOrigin = 'Anonymous' // 允许跨域图片加载（如果需要）
//
//       img.onload = () => {
//         const canvas = document.createElement('canvas')
//         const ctx = canvas.getContext('2d')
//
//         if (!ctx) {
//           reject(new Error('Unable to get canvas 2D context'))
//           return
//         }
//
//         // 设置canvas大小
//         canvas.width = img.width
//         canvas.height = img.height
//
//         // 绘制原始图片到canvas
//         ctx.drawImage(img, 0, 0)
//
//         // 获取图像数据
//         const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
//
//         // 创建 worker
//         const worker = new Worker(new URL('@/utils/imageExpanderWorker.js', import.meta.url))
//
//         // 处理 worker 发回的消息
//         worker.onmessage = function(e) {
//           const { newData } = e.data
//           const newImageData = new ImageData(newData, canvas.width, canvas.height)
//           ctx.putImageData(newImageData, 0, 0)
//           resolve(canvas.toDataURL())
//
//           // 终止worker，释放资源
//           worker.terminate()
//         }
//
//         // 错误处理
//         worker.onerror = function(error) {
//           reject(new Error(`Worker error: ${error.message}`))
//           worker.terminate()
//         }
//
//         // 发送图像数据至 worker
//         worker.postMessage({
//           data: imageData.data,
//           expandBy: expandBy,
//           width: canvas.width,
//           height: canvas.height
//         })
//       }
//
//       img.onerror = (error) => {
//         reject(new Error(`Image could not be loaded from URL: ${imageUrl}`))
//       }
//
//       img.src = url
//     })
//   })
// }

/**
 * 使用蒙版图片的非透明区域裁剪原图
 * @param originalImageUrl 原图地址
 * @param maskImageUrl 蒙版图片地址
 * @returns 裁剪后的图片地址
 */
export async function cropImageWithMaskTransparency(
  originalImageUrl: string,
  maskImageUrl: string
): Promise<string> {
  // 确保 WASM 模块已初始化
  if (!wasmInitialized) {
    await initWasm()
    console.log('wasmInitialized', wasmInitialized)
  }
  
  // 创建一个Promise用于处理整个裁剪过程，防止UI阻塞
  return new Promise<string>((resolve, reject) => {
    // 处理图像加载和裁剪过程
    const processImages = async () => {
      try {
        const originalImage = await urlParser(originalImageUrl)
        const maskImage = await urlParser(maskImageUrl)
        
        // 创建离屏Image对象，避免直接在DOM上操作
        const img = new Image()
        const maskImg = new Image()
        
        img.crossOrigin = 'anonymous'
        maskImg.crossOrigin = 'anonymous'
        
        // 使用Promise.all同时加载两张图片
        await Promise.all([
          new Promise<void>((resolveImg) => {
            img.onload = () => resolveImg()
            img.onerror = () => {
              console.error('原始图片加载失败')
              reject(new Error('原始图片加载失败'))
            }
            img.src = originalImage
          }),
          new Promise<void>((resolveMask) => {
            maskImg.onload = () => resolveMask()
            maskImg.onerror = () => {
              console.error('蒙版图片加载失败')
              reject(new Error('蒙版图片加载失败'))
            }
            maskImg.src = maskImage
          })
        ])
        
        // 创建离屏画布进行处理，避免在可见画布上直接操作
        const offscreenCanvas = document.createElement('canvas')
        const offscreenCtx = offscreenCanvas.getContext('2d')
        
        if (!offscreenCtx) {
          throw new Error('无法创建画布上下文')
        }
        
        // 使用原图尺寸设置画布大小
        offscreenCanvas.width = img.width
        offscreenCanvas.height = img.height
        
        // 在离屏画布上绘制原始图片
        offscreenCtx.drawImage(img, 0, 0, offscreenCanvas.width, offscreenCanvas.height)
        
        // 获取原始图片数据
        const imageData = offscreenCtx.getImageData(0, 0, offscreenCanvas.width, offscreenCanvas.height)
        
        // 再创建一个离屏画布用于处理蒙版
        const maskOffscreenCanvas = document.createElement('canvas')
        const maskOffscreenCtx = maskOffscreenCanvas.getContext('2d')
        
        if (!maskOffscreenCtx) {
          throw new Error('无法创建蒙版画布上下文')
        }
        
        // 设置蒙版画布大小与原图相同
        maskOffscreenCanvas.width = offscreenCanvas.width
        maskOffscreenCanvas.height = offscreenCanvas.height
        
        // 绘制蒙版图片到离屏画布，缩放到与原图相同尺寸
        maskOffscreenCtx.drawImage(maskImg, 0, 0, maskOffscreenCanvas.width, maskOffscreenCanvas.height)
        
        // 获取蒙版图片数据
        const maskData = maskOffscreenCtx.getImageData(0, 0, maskOffscreenCanvas.width, maskOffscreenCanvas.height)
        
        // 处理图像数据
        let resultData: Uint8ClampedArray
        
        try {
          // 使用JavaScript实现的蒙版裁剪功能
          resultData = fallbackCropImageWithMask(imageData.data, maskData.data)
        } catch (error) {
          console.error('蒙版裁剪处理失败:', error)
          // 创建一个空的结果（全透明）作为失败时的回退
          resultData = new Uint8ClampedArray(imageData.data.length)
          for (let i = 0; i < resultData.length; i += 4) {
            resultData[i + 3] = 0 // 设置Alpha通道为0（完全透明）
          }
        }
        
        // 创建最终的结果画布
        const resultCanvas = document.createElement('canvas')
        const resultCtx = resultCanvas.getContext('2d')
        
        if (!resultCtx) {
          throw new Error('无法创建结果画布上下文')
        }
        
        // 设置结果画布大小
        resultCanvas.width = offscreenCanvas.width
        resultCanvas.height = offscreenCanvas.height
        
        // 将处理后的图像数据放到结果画布
        const resultImageData = new ImageData(resultData, resultCanvas.width, resultCanvas.height)
        resultCtx.putImageData(resultImageData, 0, 0)
        
        // 完成所有处理后，一次性返回结果，避免中间状态
        resolve(resultCanvas.toDataURL('image/png'))
      } catch (error) {
        console.error('裁剪过程发生错误:', error)
        reject(error)
      }
    }
    
    // 启动处理流程
    processImages()
  })
}

/**
 * 使用JavaScript实现的蒙版裁剪功能
 * @param imageData 原始图片数据
 * @param maskData 蒙版图片数据
 * @returns 处理后的图片数据
 */
function fallbackCropImageWithMask(
  imageData: Uint8ClampedArray,
  maskData: Uint8ClampedArray
): Uint8ClampedArray {
  // 创建新的数据数组
  const resultData = new Uint8ClampedArray(imageData.length)
  
  // 设置透明阈值，低于此值的蒙版像素将被视为透明
  const ALPHA_THRESHOLD = 10
  
  // 遍历每个像素
  for (let i = 0; i < imageData.length; i += 4) {
    // 获取蒙版的alpha值 (第四个通道)
    const maskAlpha = maskData[i + 3]
    
    if (maskAlpha > ALPHA_THRESHOLD) {
      // 如果蒙版像素不透明，保留原始图片的该像素
      resultData[i] = imageData[i]         // 红色通道
      resultData[i + 1] = imageData[i + 1] // 绿色通道
      resultData[i + 2] = imageData[i + 2] // 蓝色通道
      resultData[i + 3] = 255              // 设置为完全不透明
    } else {
      // 如果蒙版像素透明，设置为透明
      resultData[i] = 0
      resultData[i + 1] = 0
      resultData[i + 2] = 0
      resultData[i + 3] = 0
    }
  }
  
  return resultData
}

/**
 * 用另一张图片的裁剪区域，等比例居中裁剪给定的图片，实时设计专用
 * @param base64Source
 * @param sourceWidth
 * @param sourceHeight
 * @param cropWidth
 * @param cropHeight
 */
export function cropImageUsingRatio4Realtime(
  base64Source: string,
  sourceWidth: number,
  sourceHeight: number,
  cropWidth: number,
  cropHeight: number
): Promise<string> {
  return new Promise((resolve) => {
    // 创建一个新的 Image 对象
    const img = new Image()
    img.src = base64Source

    img.onload = () => {
      // 计算裁剪比例
      const ratioX = cropWidth / sourceWidth
      const ratioY = cropHeight / sourceHeight

      // 应用比例到图片一
      const targetWidth = img.width * ratioX
      const targetHeight = img.height * ratioY

      // 计算裁剪起始点以确保居中
      const x = (img.width - targetWidth) / 2
      const y = (img.height - targetHeight) / 2

      // 创建 canvas 并设置裁剪尺寸
      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight

      const ctx = canvas.getContext('2d')

      // 在 canvas 上绘制裁剪后的图片
      ctx!.drawImage(img, x, y, targetWidth, targetHeight, 0, 0, targetWidth, targetHeight)

      // 将 canvas 转换为 base64
      resolve(canvas.toDataURL('image/jpeg'))
    }

    img.onerror = () => {
      console.log('图片加载失败')
    }
  })
}

/**
 * 长边缩放成给定尺寸，短边等比例缩放，实时设计专用
 * @param base64
 * @param maxLength
 */
export function resizeBase64Img4Realtime(base64: string, maxLength: number): Promise<string> {
  return new Promise((resolve, reject) => {
    urlParser(base64).then((url) => {
      // 创建Image对象
      const img = new Image()
      img.onload = () => {
        // 获取原始尺寸
        const width = img.width
        const height = img.height

        // 计算缩放比例
        const scalingFactor = maxLength / Math.max(width, height)
        const newWidth = width * scalingFactor
        const newHeight = height * scalingFactor

        // 创建canvas
        const canvas = document.createElement('canvas')
        canvas.width = newWidth
        canvas.height = newHeight
        const ctx = canvas.getContext('2d')

        // 在canvas上绘制调整大小后的图像
        ctx?.drawImage(img, 0, 0, newWidth, newHeight)

        // 导出新的base64字符串
        const newDataUrl = canvas.toDataURL('image/jpeg', '0.95')
        resolve(newDataUrl)
      }

      img.onerror = reject

      // 设置原始base64数据
      img.src = url
    })
  })
}

/**
 * 图片转灰度图
 * @param imageUrl
 * @param invert
 */
export async function imageToTransparencyMappedGrayScale(
  imageUrl: string,
  invert = false
): Promise<string> {
  // 确保 WASM 模块已初始化
  if (!wasmInitialized) {
    await initWasm();
    console.log('wasmInitialized', wasmInitialized);
  }
  
  const img = new Image();
  img.crossOrigin = 'Anonymous'; // 解决跨域访问问题
  img.src = await urlParser(imageUrl);
  
  return new Promise<string>((resolve, reject) => {
    img.onload = function() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let resultData: Uint8ClampedArray;
      
      if (wasmInitialized) {
        try {
          // 使用 WASM 函数处理图像数据
          const result = maskProcessorModule.transparency_to_grayscale(
            new Uint8Array(imageData.data.buffer),
            canvas.width,
            canvas.height,
            invert
          );
          resultData = new Uint8ClampedArray(result.buffer);
          console.info('transparency_to_grayscale with wasm success');
        } catch (error) {
          console.error('Error using WASM to convert transparency to grayscale:', error);
          // 回退到 JavaScript 实现
          resultData = applyTransparencyToGrayscaleJS(imageData.data, invert);
        }
      } else {
        console.info('transparency_to_grayscale fallback');
        // 回退到 JavaScript 实现
        resultData = applyTransparencyToGrayscaleJS(imageData.data, invert);
      }
      
      // 更新图像数据
      const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
      ctx.putImageData(outputImageData, 0, 0);
      
      // 返回处理后的图像
      resolve(canvas.toDataURL('image/png'));
    };
    
    img.onerror = function(error) {
      reject(error);
    };
  });
}

// JavaScript 版本的图像处理，作为回退
function applyTransparencyToGrayscaleJS(
  data: Uint8ClampedArray,
  invert: boolean
): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(data.length);
  
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3] / 255;
    const gray = Math.round(alpha * 255);
    
    if (invert) {
      result[i] = 255 - gray;
      result[i + 1] = 255 - gray;
      result[i + 2] = 255 - gray;
      result[i + 3] = 255;
    } else {
      result[i] = gray;
      result[i + 1] = gray;
      result[i + 2] = gray;
      result[i + 3] = 255;
    }
    
    if (alpha === 0) {
      result[i] = invert ? 255 : 0;
      result[i + 1] = invert ? 255 : 0;
      result[i + 2] = invert ? 255 : 0;
      result[i + 3] = 255;
    }
  }
  
  return result;
}

export async function blobURL2Blob(blobURL: string): Promise<Blob | null> {
  if (!blobURL.startsWith('blob:')) return null
  const response = await fetch(blobURL)
  return response.blob()
}

export function renderToolbar({ nodes }: ImageRenderToolbarProps) {
  return [
    nodes.resizeToOriginalSize,
    nodes.zoomIn,
    nodes.zoomOut,
    nodes.close
  ]
}

/**
 * 根据蒙版，擦除图片内容
 * @param imageUrl
 * @param maskUrl
 */
export async function eraseImageContent(imageUrl: string, maskUrl: string): Promise<string> {
  // 确保 WASM 模块已初始化
  if (!wasmInitialized) {
    await initWasm();
    console.log('wasmInitialized', wasmInitialized);
  }
  
  const image = await urlParser(imageUrl);
  const mask = await urlParser(maskUrl);
  
  const img = new Image();
  const maskImg = new Image();
  
  img.crossOrigin = 'anonymous';
  maskImg.crossOrigin = 'anonymous';
  
  // 加载原始图片和蒙版图片
  await Promise.all([
    new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => {
        console.error('原始图片加载失败');
        resolve(); // 继续执行，让后续错误处理来处理
      };
      img.src = image;
    }),
    new Promise<void>((resolve) => {
      maskImg.onload = () => resolve();
      maskImg.onerror = () => {
        console.error('蒙版图片加载失败');
        resolve(); // 继续执行，让后续错误处理来处理
      };
      maskImg.src = mask;
    })
  ]);
  
  // 创建 canvas 用于获取图像数据
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('无法获取 canvas 上下文');
  }
  
  // 设置画布尺寸为原图尺寸
  canvas.width = img.width;
  canvas.height = img.height;
  
  // 获取原始图片的数据
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  
  // 创建临时 canvas 来处理蒙版
  const maskCanvas = document.createElement('canvas')
  const maskCtx = maskCanvas.getContext('2d')
  
  if (!maskCtx) {
    throw new Error('无法创建蒙版画布上下文')
  }
  
  // 设置蒙版画布大小与原图相同
  maskCanvas.width = canvas.width
  maskCanvas.height = canvas.height
  
  // 绘制蒙版图片，缩放到与原图相同尺寸
  maskCtx.drawImage(maskImg, 0, 0, maskCanvas.width, maskCanvas.height)
  
  // 获取蒙版图片数据
  const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height)
  
  // 处理图像数据
  let resultData: Uint8ClampedArray
  
  if (wasmInitialized) {
    try {
      // 使用 WASM 函数处理图像数据
      const result = maskProcessorModule.erase_image_content(
        new Uint8Array(imageData.data.buffer),
        new Uint8Array(maskData.data.buffer),
        canvas.width,
        canvas.height,
        240 // 设置阈值，与原始代码一致
      );
      resultData = new Uint8ClampedArray(result.buffer);
      console.info('erase_image_content with wasm success');
    } catch (error) {
      console.error('Error using WASM to erase image content:', error);
      // 回退到 JavaScript 实现
      resultData = applyEraseImageContentJS(imageData.data, maskData.data);
    }
  } else {
    console.info('erase_image_content fallback');
    // 回退到 JavaScript 实现
    resultData = applyEraseImageContentJS(imageData.data, maskData.data);
  }
  
  // 将处理后的图像数据放回 canvas
  const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
  ctx.putImageData(outputImageData, 0, 0);
  
  // 返回处理后的图像
  return canvas.toDataURL('image/png');
}

// JavaScript 版本的图像处理，作为回退
function applyEraseImageContentJS(
  imageData: Uint8ClampedArray,
  maskData: Uint8ClampedArray
): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(imageData.length);
  result.set(imageData);
  
  // 根据蒙版的灰度值来擦除原图内容
  for (let i = 0; i < result.length; i += 4) {
    // 计算蒙版像素的灰度值
    const r = maskData[i];
    const g = maskData[i + 1];
    const b = maskData[i + 2];
    
    // 计算灰度值 (0-255)
    const gray = (r + g + b) / 3;
    
    // 如果是白色（灰度值接近255），则擦除
    // 添加一个阈值，避免灰色区域的干扰
    if (gray > 240) {
      result[i + 3] = 0;  // 完全透明
    }
  }
  
  return result;
}

// 图片去色
export const desaturateImage = async (image: string) => {
  try {
    // 确保 WASM 模块已初始化
    if (!wasmInitialized) {
      await initWasm();
      console.log('wasmInitialized', wasmInitialized);
    }
    
    // 创建一个新的Image元素加载图片
    const img = new Image()
    img.crossOrigin = 'anonymous' // 设置跨域属性，以防止安全错误
    // 使用Promise等待图片加载完成
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = image
    })

    // 创建Canvas元素
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('无法获取Canvas 2D上下文')
    }

    // 设置Canvas尺寸与图片一致
    canvas.width = img.width
    canvas.height = img.height

    // 在Canvas上绘制原图
    ctx.drawImage(img, 0, 0)

    // 获取图像数据
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    
    // 处理图像数据
    let resultData: Uint8ClampedArray;
    
    if (wasmInitialized) {
      try {
        // 尝试使用WASM处理图像
        const result = maskProcessorModule.desaturate_image(
          new Uint8Array(imageData.data.buffer),
          canvas.width,
          canvas.height
        );
        resultData = new Uint8ClampedArray(result.buffer);
        console.info('desaturateImage with wasm success');
      } catch (error) {
        console.error('Error using WASM to desaturate image:', error);
        // 回退到JavaScript实现
        resultData = applyDesaturateJS(imageData.data);
      }
    } else {
      console.info('desaturateImage fallback to JS');
      // 回退到JavaScript实现
      resultData = applyDesaturateJS(imageData.data);
    }
    
    // 将处理后的图像数据放回Canvas
    const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
    ctx.putImageData(outputImageData, 0, 0);

    // 将Canvas转换为base64图片并返回
    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('去色处理图片时出错:', error)
    // 出错时返回原图
    return image
  }
}

// JavaScript版本的图像去色实现，作为回退
function applyDesaturateJS(data: Uint8ClampedArray): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(data.length);
  
  // 对每个像素进行灰度处理
  for (let i = 0; i < data.length; i += 4) {
    // 使用加权平均法计算灰度值 (0.299*R + 0.587*G + 0.114*B)
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

    // 设置RGB通道为相同的灰度值，保留原始Alpha通道
    result[i] = gray; // 红色通道
    result[i + 1] = gray; // 绿色通道
    result[i + 2] = gray; // 蓝色通道
    result[i + 3] = data[i + 3]; // 保持Alpha通道不变
  }
  
  return result;
}

// 提取图片阴影层
export const extractImageShadow = async (image: string, shadowLevelOffset = 0, shadowRange = 0.99) => {
  try {
    // 确保 WASM 模块已初始化
    if (!wasmInitialized) {
      await initWasm();
      console.log('wasmInitialized', wasmInitialized);
    }
    
    // 创建一个新的Image元素加载图片
    const img = new Image()
    img.crossOrigin = 'anonymous' // 设置跨域属性，以防止安全错误
    // 使用Promise等待图片加载完成
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = image
    })

    // 创建Canvas元素
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('无法获取Canvas 2D上下文')
    }

    // 设置Canvas尺寸与图片一致
    canvas.width = img.width
    canvas.height = img.height

    // 在Canvas上绘制原图
    ctx.drawImage(img, 0, 0)

    // 获取图像数据
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    
    // 从ComfyUI实际效果分析，阴影层实际是高光的负片
    // 参数调整 - shadowLevelOffset的值越大，阴影区域越少
    const offset = shadowLevelOffset / 100
    
    // 处理图像数据
    let resultData: Uint8ClampedArray;
    
    if (wasmInitialized) {
      try {
        // 尝试使用WASM处理图像
        const result = maskProcessorModule.extract_image_shadow(
          new Uint8Array(imageData.data.buffer),
          canvas.width,
          canvas.height,
          offset,
          shadowRange
        );
        resultData = new Uint8ClampedArray(result.buffer);
        console.info('extractImageShadow with wasm success');
      } catch (error) {
        console.error('Error using WASM to extract shadow:', error);
        // 回退到JavaScript实现
        resultData = applyExtractShadowJS(imageData.data, offset, shadowRange);
      }
    } else {
      console.info('extractImageShadow fallback to JS');
      // 回退到JavaScript实现
      resultData = applyExtractShadowJS(imageData.data, offset, shadowRange);
    }
    
    // 将处理后的图像数据放回Canvas
    const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
    ctx.putImageData(outputImageData, 0, 0);

    // 将Canvas转换为base64图片并返回
    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('提取图片阴影层时出错:', error)
    // 出错时返回原图
    return image
  }
}

// JavaScript版本的提取阴影实现，作为回退
function applyExtractShadowJS(data: Uint8ClampedArray, offset: number, shadowRange: number): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(data.length);
  
  // 对每个像素进行处理
  for (let i = 0; i < data.length; i += 4) {
    // 计算像素的亮度值 (0.299*R + 0.587*G + 0.114*B)
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]

    // 归一化到0-1范围
    const normalizedGray = gray / 255

    // 直接将图像反相，亮的变暗，暗的变亮
    let value = 1 - normalizedGray

    // 应用偏移参数 - 偏移越大，暗部区域变得更暗
    value = Math.max(0, value - offset)

    // 应用范围参数 - 控制过渡的平滑程度
    if (shadowRange !== 1) {
      value = Math.pow(value, 1 / shadowRange)
    }

    // 将值缩放回0-255范围
    const pixelValue = Math.round(value * 255)

    // 应用到所有颜色通道
    result[i] = pixelValue // 红色通道
    result[i + 1] = pixelValue // 绿色通道
    result[i + 2] = pixelValue // 蓝色通道
    result[i + 3] = data[i + 3] // 保持Alpha通道不变
  }
  
  return result;
}

// 提取图片高光层
export const extractImageHighlight = async (
  image: string,
  highlightLevelOffset = 25,
  highlightRange = 0.99
) => {
  try {
    // 确保 WASM 模块已初始化
    if (!wasmInitialized) {
      await initWasm();
      console.log('wasmInitialized', wasmInitialized);
    }
    
    // 创建一个新的Image元素加载图片
    const img = new Image()
    img.crossOrigin = 'anonymous' // 设置跨域属性，以防止安全错误
    // 使用Promise等待图片加载完成
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = image
    })

    // 创建Canvas元素
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('无法获取Canvas 2D上下文')
    }

    // 设置Canvas尺寸与图片一致
    canvas.width = img.width
    canvas.height = img.height

    // 在Canvas上绘制原图
    ctx.drawImage(img, 0, 0)

    // 获取图像数据
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    
    // 参考ComfyUI的LayerMask: Shadow & Highlight Mask实现
    // 调整偏移值到0-1范围
    const offset = highlightLevelOffset / 100
    
    // 处理图像数据
    let resultData: Uint8ClampedArray;
    
    if (wasmInitialized) {
      try {
        // 尝试使用WASM处理图像
        const result = maskProcessorModule.extract_image_highlight(
          new Uint8Array(imageData.data.buffer),
          canvas.width,
          canvas.height,
          offset,
          highlightRange
        );
        resultData = new Uint8ClampedArray(result.buffer);
        console.info('extractImageHighlight with wasm success');
      } catch (error) {
        console.error('Error using WASM to extract highlight:', error);
        // 回退到JavaScript实现
        resultData = applyExtractHighlightJS(imageData.data, offset, highlightRange);
      }
    } else {
      console.info('extractImageHighlight fallback to JS');
      // 回退到JavaScript实现
      resultData = applyExtractHighlightJS(imageData.data, offset, highlightRange);
    }
    
    // 将处理后的图像数据放回Canvas
    const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
    ctx.putImageData(outputImageData, 0, 0);

    // 将Canvas转换为base64图片并返回
    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('提取图片高光层时出错:', error)
    // 出错时返回原图
    return image
  }
}

// JavaScript版本的提取高光实现，作为回退
function applyExtractHighlightJS(data: Uint8ClampedArray, offset: number, highlightRange: number): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(data.length);
  
  // 对每个像素进行处理，提取高光（亮部）信息
  for (let i = 0; i < data.length; i += 4) {
    // 计算像素的亮度值 (0.299*R + 0.587*G + 0.114*B)，归一化到0-1
    const luminance = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255

    // 计算高光遮罩值 - 直接使用亮度值
    let highlightMask = luminance

    // 应用偏移 - 值越大，需要越亮的区域才会被视为高光
    highlightMask = Math.max(0, highlightMask - offset)

    // 应用范围参数调整过渡平滑度
    // 当highlightRange接近1时，过渡更平滑；接近0时，对比度更高
    if (highlightRange !== 1) {
      highlightMask = Math.pow(highlightMask, 1 / highlightRange)
    }

    // 将处理后的值转换回0-255范围并应用到所有通道
    const value = Math.round(highlightMask * 255)
    result[i] = value // 红色通道
    result[i + 1] = value // 绿色通道
    result[i + 2] = value // 蓝色通道
    result[i + 3] = data[i + 3] // 保持Alpha通道不变
  }
  
  return result;
}

// 透明图像裁剪
export const cropTransparentImage = async (image: string, mask: string) => {
  try {
    // 确保 WASM 模块已初始化
    if (!wasmInitialized) {
      await initWasm();
      console.log('wasmInitialized', wasmInitialized);
    }
    
    // 创建Image元素加载原始图像
    const img = new Image()
    img.crossOrigin = 'anonymous' // 设置跨域属性，以防止安全错误
    // 使用Promise等待图片加载完成
    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
      img.src = image
    })

    // 创建Image元素加载遮罩图像
    const maskImg = new Image()
    maskImg.crossOrigin = 'anonymous' // 设置跨域属性，以防止安全错误
    // 使用Promise等待图片加载完成
    await new Promise((resolve, reject) => {
      maskImg.onload = resolve
      maskImg.onerror = reject
      maskImg.src = mask
    })

    // 创建Canvas元素
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('无法获取Canvas 2D上下文')
    }

    // 设置Canvas尺寸与图片一致
    canvas.width = img.width
    canvas.height = img.height

    // 清空画布并设置为透明
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // 先绘制遮罩到一个临时canvas中以便获取其数据
    const maskCanvas = document.createElement('canvas')
    const maskCtx = maskCanvas.getContext('2d')
    if (!maskCtx) {
      throw new Error('无法获取遮罩Canvas 2D上下文')
    }

    maskCanvas.width = img.width
    maskCanvas.height = img.height

    // 绘制并调整遮罩大小以匹配原图
    maskCtx.drawImage(maskImg, 0, 0, img.width, img.height)

    // 获取遮罩数据
    const maskData = maskCtx.getImageData(0, 0, img.width, img.height)

    // 绘制原图
    ctx.drawImage(img, 0, 0)

    // 获取原图数据
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    
    // 处理图像和遮罩数据
    let resultData: Uint8ClampedArray;
    
    if (wasmInitialized) {
      try {
        // 尝试使用WASM处理图像
        const result = maskProcessorModule.crop_transparent_image(
          new Uint8Array(imgData.data.buffer),
          new Uint8Array(maskData.data.buffer),
          canvas.width,
          canvas.height
        );
        resultData = new Uint8ClampedArray(result.buffer);
        console.info('cropTransparentImage with wasm success');
      } catch (error) {
        console.error('Error using WASM to crop transparent image:', error);
        // 回退到JavaScript实现
        resultData = applyCropTransparentJS(imgData.data, maskData.data);
      }
    } else {
      console.info('cropTransparentImage fallback to JS');
      // 回退到JavaScript实现
      resultData = applyCropTransparentJS(imgData.data, maskData.data);
    }
    
    // 创建新的ImageData并放回Canvas
    const outputImageData = new ImageData(resultData, canvas.width, canvas.height);
    ctx.putImageData(outputImageData, 0, 0);

    // 查找非透明区域的边界
    let minX = canvas.width
    let minY = canvas.height
    let maxX = 0
    let maxY = 0

    // 分析图像数据找到非透明区域的边界
    const data = outputImageData.data;
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const idx = (y * canvas.width + x) * 4
        // 如果像素不完全透明
        if (data[idx + 3] > 10) {
          // 使用小阈值避免边缘噪点
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }
    }

    // 如果没有找到非透明区域，返回原图
    if (minX >= maxX || minY >= maxY) {
      return image
    }

    // 添加适当的内边距
    const padding = 5
    minX = Math.max(0, minX - padding)
    minY = Math.max(0, minY - padding)
    maxX = Math.min(canvas.width - 1, maxX + padding)
    maxY = Math.min(canvas.height - 1, maxY + padding)

    // 创建裁剪Canvas
    const croppedCanvas = document.createElement('canvas')
    const croppedCtx = croppedCanvas.getContext('2d')
    if (!croppedCtx) {
      throw new Error('无法获取裁剪Canvas 2D上下文')
    }

    // 设置裁剪Canvas的尺寸
    const width = maxX - minX + 1
    const height = maxY - minY + 1
    croppedCanvas.width = width
    croppedCanvas.height = height

    // 将原Canvas的非透明区域绘制到裁剪Canvas上
    croppedCtx.drawImage(canvas, minX, minY, width, height, 0, 0, width, height)

    // 将裁剪后的图像转换为DataURL并返回
    return croppedCanvas.toDataURL('image/png')
  } catch (error) {
    console.error('裁剪透明图像时出错:', error)
    return image // 发生错误时返回原图
  }
}

// JavaScript版本的透明图像裁剪实现，作为回退
function applyCropTransparentJS(imgData: Uint8ClampedArray, maskData: Uint8ClampedArray): Uint8ClampedArray {
  // 创建一个副本以保留原始数据
  const result = new Uint8ClampedArray(imgData.length);
  
  // 模拟PIL的paste with mask操作：使用遮罩alpha通道
  for (let i = 0; i < imgData.length; i += 4) {
    // 获取原图像素
    result[i] = imgData[i];
    result[i + 1] = imgData[i + 1];
    result[i + 2] = imgData[i + 2];
    
    // 获取遮罩对应位置的亮度值（R通道即可，因为遮罩通常是灰度图）
    const maskAlpha = maskData[i];
    
    // 设置原图像素的透明度与遮罩值成正比
    result[i + 3] = maskAlpha; // 直接使用遮罩值作为Alpha通道
  }
  
  return result;
}
